import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Result } from '../interfaces/helpers';
import { IEntitlement, IPermission, IUser, User } from '../models';
import {
  ACTIONS,
  CentralApis,
  Endpoint,
  PageConfig,
} from '../interfaces/internal';
import { PageTitles } from '../interfaces/types';
import { Page } from './page';
import { PermissionDTO } from '../interfaces/dtos';

export class UserPage extends Page<User> {
  permissionUrl: string;
  userUrl: string;

  constructor({ endpoint }: Endpoint, config: PageConfig) {
    super((endpoint as CentralApis).iam + 'user', PageTitles.user, config);
    this.userUrl = (endpoint as CentralApis).iam + 'user';
    this.permissionUrl = (endpoint as CentralApis).iam + 'permission';

    this.initializeColumns({
      keys: new User(),
      editableFields: this.editableFields,
      hiddenTableFields: this.hiddenTableFields,
      hiddenFormFields: this.hiddenFieldsForm,
    });
  }

  get actions(): string[] {
    return [ACTIONS.delete];
  }

  get hiddenFieldsForm(): string[] {
    return ['href', 'createdAt', 'modifiedAt', 'createdBy', 'modifiedBy'];
  }

  get hiddenTableFields(): string[] {
    return [
      'createdAt',
      'modifiedAt',
      'createdBy',
      'modifiedBy',
      'href',
      'role',
      'roleId',
      'clientId',
    ];
  }

  get editableFields(): string[] {
    return ['active', 'roleName'];
  }

  delete(id: string): Observable<IUser> {
    const user = this.pageItems.getValue().find((usr) => usr.id === id);
    return super.delete(user.userName);
  }

  addRole(permission: PermissionDTO): Observable<IPermission> {
    return this.dataService
      .postDTO<IPermission, PermissionDTO>(this.permissionUrl, permission)
      .pipe(map((result) => result));
  }

  getPermission(userName: string): Observable<Result<IEntitlement>> {
    return this.dataService
      .get<Result<IEntitlement>>(this.permissionUrl, { userName })
      .pipe(map((result) => result));
  }

  updateRole(
    permission: PermissionDTO,
    privilegeID
  ): Observable<PermissionDTO> {
    return this.dataService
      .put<PermissionDTO>(this.permissionUrl + '/' + privilegeID, permission)
      .pipe(map((result) => result));
  }

  fetch(queryString?): Observable<Result<User>> {
    return this.getItems(queryString).pipe(
      tap(({ items }) => {
        this.pageItems.next(items);
      })
    );
  }

  fetchById(id: string): Observable<User> {
    return this.getItem(id).pipe(
      map((data: IUser) => this.createFromDTO(data))
    );
  }

  update(user: User): Observable<IUser> {
    let updatedUsers: IUser[];

    return this.items.pipe(
      take(1),
      switchMap((users) => {
        if (!users || users.length <= 0) {
          return this.fetch().pipe(map(({ items }) => items));
        } else {
          return of(users);
        }
      }),
      switchMap((users) => {
        const userIndex = users.findIndex((i) => i.id === user.id);
        updatedUsers = [...users];
        updatedUsers[userIndex] = new User({
          clientId: user.clientId,
          id: user.userName,
          createdAt: user.createdAt,
          modifiedAt: user.modifiedAt,
          createdBy: user.createdBy,
          modifiedBy: user.modifiedBy,
          href: user.href,
          userName: user.userName,
          roleName: user.roleName,
          roleId: user.roleId,
          active: user.active,
        });
        return this.updateItem(
          {
            active: updatedUsers[userIndex].active,
          },
          updatedUsers[userIndex].userName
        );
      }),
      tap(() => {
        this.pageItems.next(updatedUsers);
      })
    );
  }

  createFromDTO(item): User {
    return new User({
      id: item.id,
      clientId: item.clientId,
      createdAt: item.createdAt,
      modifiedAt: item.modifiedAt,
      createdBy: item.createdBy,
      modifiedBy: item.modifiedBy,
      href: item.href,
      userName: item.userName,
      roleName: item.roleName,
      roleId: item.roleId,
      active: item.active,
    });
  }
}
